import * as React from 'react';
import { isReactRenderable, pickAttrs, useEvent } from '@rc-component/util';
import { clsx } from 'clsx';
import { useMergeSemantic, useSemanticRootStyle } from '../../_util/hooks/useMergeSemantic';
import { useSyncState } from '../../_util/hooks';
import { isFunction } from '../../_util/is';
import { getMergedStatus } from '../../_util/statusUtils';
import { devUseWarning } from '../../_util/warning';
import { useComponentConfig } from '../../config-provider/context';
import useSize from '../../config-provider/hooks/useSize';
import { FormItemInputContext } from '../../form/context';
import useVariant from '../../form/hooks/useVariants';
import useStyle from '../style/otp';
import OTPInput from './OTPInput';
function strToArr(str) {
    return (str || '').split('');
}
const Separator = (props) => {
    const { index, prefixCls, separator, className: semanticClassName, style: semanticStyle } = props;
    const separatorNode = isFunction(separator) ? separator(index) : separator;
    if (!isReactRenderable(separatorNode)) {
        return null;
    }
    return (React.createElement("span", { className: clsx(`${prefixCls}-separator`, semanticClassName), style: semanticStyle }, separatorNode));
};
const OTP = React.forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, length = 6, size: customSize, defaultValue, value, onChange, formatter, separator, variant: customizeVariant, disabled, status: customStatus, autoFocus, mask, type, autoComplete, onInput, onFocus, inputMode, classNames, styles, rootClassName, className, style, ...restProps } = props;
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('Input.OTP');
        warning(!(typeof mask === 'string' && mask.length > 1), 'usage', '`mask` prop should be a single character.');
    }
    const { classNames: contextClassNames, styles: contextStyles, getPrefixCls, direction, style: contextStyle, className: contextClassName, } = useComponentConfig('otp');
    const prefixCls = getPrefixCls('otp', customizePrefixCls);
    const [variant] = useVariant('otp', customizeVariant, undefined, 'input');
    const mergedProps = {
        ...props,
        length,
        variant,
    };
    const contextStyleRoot = useSemanticRootStyle(contextStyle);
    const styleRoot = useSemanticRootStyle(style);
    const [mergedClassNames, mergedStyles] = useMergeSemantic([contextClassNames, classNames], [contextStyles, contextStyleRoot, styles, styleRoot], {
        props: mergedProps,
    });
    const domAttrs = pickAttrs(restProps, {
        aria: true,
        data: true,
        attr: true,
    });
    // ========================= Root =========================
    // Style
    const [hashId, cssVarCls] = useStyle(prefixCls);
    // ========================= Size =========================
    const mergedSize = useSize((ctx) => customSize ?? ctx);
    // ======================== Status ========================
    const formContext = React.useContext(FormItemInputContext);
    const mergedStatus = getMergedStatus(formContext.status, customStatus);
    const proxyFormContext = React.useMemo(() => ({
        ...formContext,
        status: mergedStatus,
        hasFeedback: false,
        feedbackIcon: null,
    }), [formContext, mergedStatus]);
    // ========================= Refs =========================
    const containerRef = React.useRef(null);
    const inputsRef = React.useRef({});
    React.useImperativeHandle(ref, () => ({
        focus: () => {
            inputsRef.current[0]?.focus();
        },
        blur: () => {
            for (let i = 0; i < length; i += 1) {
                inputsRef.current[i]?.blur();
            }
        },
        nativeElement: containerRef.current,
    }));
    // ======================= Formatter ======================
    const internalFormatter = (txt) => (formatter ? formatter(txt) : txt);
    // ======================== Values ========================
    const [getValueCells, setValueCells] = useSyncState(strToArr(internalFormatter(defaultValue || '')));
    const valueCells = getValueCells();
    React.useEffect(() => {
        if (value !== undefined) {
            setValueCells(strToArr(value));
        }
    }, [value]);
    const triggerValueCellsChange = useEvent((nextValueCells) => {
        const prevCells = getValueCells();
        setValueCells(nextValueCells);
        if (onInput) {
            onInput(nextValueCells);
        }
        // Trigger if all cells are filled
        if (onChange &&
            nextValueCells.length === length &&
            nextValueCells.every((c) => c) &&
            nextValueCells.some((c, index) => prevCells[index] !== c)) {
            onChange(nextValueCells.join(''));
        }
    });
    const patchValue = useEvent((index, txt) => {
        let nextCells = [...valueCells];
        // Fill cells till index
        for (let i = 0; i < index; i += 1) {
            if (!nextCells[i]) {
                nextCells[i] = '';
            }
        }
        if (txt.length <= 1) {
            nextCells[index] = txt;
        }
        else {
            nextCells = nextCells.slice(0, index).concat(strToArr(txt));
        }
        // Clean the last empty cell
        for (let i = nextCells.length - 1; i >= 0; i -= 1) {
            if (nextCells[i]) {
                break;
            }
            nextCells.pop();
        }
        // Format before limiting the length to avoid losing valid characters.
        const formattedValue = internalFormatter(nextCells.map((c) => c || ' ').join(''));
        nextCells = strToArr(formattedValue).map((c, i) => {
            if (c === ' ' && !nextCells[i]) {
                return nextCells[i];
            }
            return c;
        });
        return nextCells.slice(0, length);
    });
    // ======================== Change ========================
    const onInputChange = (index, txt) => {
        const nextCells = patchValue(index, txt);
        const nextIndex = Math.min(index + txt.length, length - 1);
        // Update cells before moving focus so that the focus guard reads fresh values.
        triggerValueCellsChange(nextCells);
        if (nextIndex !== index && nextCells[index] !== undefined) {
            inputsRef.current[nextIndex]?.focus();
        }
    };
    const onInputActiveChange = (nextIndex) => {
        inputsRef.current[nextIndex]?.focus();
    };
    // ======================== Focus ========================
    const onInputFocus = (event, index) => {
        // keep focus on the first empty cell
        for (let i = 0; i < index; i += 1) {
            if (!getValueCells()[i]) {
                inputsRef.current[i]?.focus();
                break;
            }
        }
        onFocus?.(event);
    };
    // ======================== Render ========================
    const inputSharedProps = {
        variant,
        disabled,
        status: mergedStatus,
        mask,
        type,
        inputMode,
        autoComplete,
    };
    return (React.createElement("div", { ...domAttrs, ref: containerRef, className: clsx(className, rootClassName, prefixCls, {
            [`${prefixCls}-sm`]: mergedSize === 'small',
            [`${prefixCls}-lg`]: mergedSize === 'large',
            [`${prefixCls}-rtl`]: direction === 'rtl',
        }, cssVarCls, hashId, contextClassName, mergedClassNames.root), style: mergedStyles.root, role: "group" },
        React.createElement(FormItemInputContext.Provider, { value: proxyFormContext }, Array.from({ length }).map((_, index) => {
            const key = `otp-${index}`;
            const singleValue = valueCells[index] || '';
            return (React.createElement(React.Fragment, { key: key },
                React.createElement(OTPInput, { ref: (inputEle) => {
                        inputsRef.current[index] = inputEle;
                    }, index: index, size: mergedSize, htmlSize: 1, className: clsx(mergedClassNames.input, `${prefixCls}-input`), style: mergedStyles.input, onChange: onInputChange, value: singleValue, onActiveChange: onInputActiveChange, autoFocus: index === 0 && autoFocus, onFocus: (event) => onInputFocus(event, index), ...inputSharedProps }),
                index < length - 1 && (React.createElement(Separator, { separator: separator, index: index, prefixCls: prefixCls, className: clsx(mergedClassNames.separator), style: mergedStyles.separator }))));
        }))));
});
export default OTP;
